import HealthData from "@/common/types/healthData";
import { request } from "@/common/api";
import { Buttons } from "@/components/atoms/Buttons";
import { Loading } from "@/components/atoms/Loading";
import { TabList } from "@/components/molecules/TabList";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertText } from "@/components/atoms/AlertText";
import { HealthList } from "@/components/molecules/HealthList";
import { TabContent } from "@/components/organisms/TabContent";
import { useNavigate } from "react-router";
import { MainTemplate } from "@/components/templates/MainTemplate";
import { useRecoilValue } from "recoil";
import { GET_HEALTH_INFO } from "@/common/querykeys";
import { ReactComponent as IconCamera } from "@/styles/assets/svg/icon_camera.svg";
import {
  guardianUserState,
  loginTypeState,
  studentUserState,
} from "@/common/states/user";
import { showAlert } from "@/components/containers/Alert";

export const SpinePage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("health");
  const loginType = useRecoilValue(loginTypeState);
  const guardian = useRecoilValue(guardianUserState);
  const student = useRecoilValue(studentUserState);

  const { data: healthData } = useQuery<HealthData>({
    queryKey: [GET_HEALTH_INFO],
    queryFn: async () => {
      const studentId =
        loginType === "student" ? student?.id : guardian?.student?.id;

      return await request<HealthData>({
        method: "GET",
        url: `/physical-exam/my-health/${studentId}`,
      });
    },
    enabled: Boolean(guardian.student?.id || student.id),
  });

  const handlePhotoConsent = () => {
    if (loginType === "guardian") {
      if (guardian.student) {
        navigate("/spine/photo-agree");
      } else {
        showAlert("서비스 이용을 위해 학생 계정을 \n연결해 주시기 바랍니다.");
      }
    } else {
      navigate("/spine/photo-agree");
    }
  };

  return (
    <MainTemplate
      nav={true}
      subPage={false}
      menuBtn={true}
      pageTitle="나의 척추건강"
    >
      <>
        {healthData?.hasData ? (
          <TabContent
            tabList={
              <TabList
                data={[
                  {
                    id: "health",
                    title: "건강정보",
                  },
                  {
                    id: "physical",
                    title: "신체기능",
                  },
                ]}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            }
          >
            <HealthList
              data={healthData.data}
              activeTab={activeTab}
              handlePhotoConsent={handlePhotoConsent}
            />
          </TabContent>
        ) : (
          <>
            {healthData?.hasScoliosisResult ? (
              <>
                <TabContent
                  tabList={
                    <TabList
                      data={[
                        {
                          id: "health",
                          title: "건강정보",
                        },
                        {
                          id: "physical",
                          title: "신체기능",
                        },
                      ]}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                    />
                  }
                >
                  <HealthList
                    data={healthData.data}
                    activeTab={activeTab}
                    handlePhotoConsent={handlePhotoConsent}
                  />
                </TabContent>
              </>
            ) : (
              <AlertText
                mainText="수집된 건강 정보가 없습니다."
                subText={
                  <>
                    {!healthData?.hasScoliosisResult ? (
                      <>
                        척추 사진 등록 및 건강 정보 입력을 해주세요. <br />
                        *사진 등록 및 건강 정보 입력을 하지 않아도 [운동하기]는
                        가능합니다.
                      </>
                    ) : (
                      "신체기능 검사 참여가 필요합니다."
                    )}
                  </>
                }
                button={
                  !healthData?.hasScoliosisResult ? (
                    <Buttons
                      type="button"
                      size="md"
                      width="176px"
                      layout="outline"
                      color="primary100"
                      icon={<IconCamera width={24} height={24} />}
                      label="척추 사진 등록하기"
                      onClick={handlePhotoConsent}
                    />
                  ) : (
                    <></>
                  )
                }
              />
            )}
          </>
        )}
      </>
    </MainTemplate>
  );
};
